import { memo, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { TrendingUp, Zap, Eye, Clock, CheckCircle } from 'lucide-react'

interface NumberPulseProps {
  number: number
  type: 'sold' | 'reserved' | 'viewing' | 'expiring' | 'activity'
  duration?: number
  intensity?: 'low' | 'medium' | 'high'
  showIcon?: boolean
  className?: string
}

/**
 * Contextual pulse animation for number grid cells
 * Shows subtle activity indicators directly on relevant numbers
 */
const NumberPulse = memo(({
  number,
  type,
  duration = 3000,
  intensity = 'medium',
  showIcon = true,
  className = ''
}: NumberPulseProps) => {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false)
    }, duration)

    return () => clearTimeout(timer)
  }, [duration])

  const getPulseConfig = () => {
    switch (type) {
      case 'sold':
        return {
          color: 'from-green-500/60 to-emerald-600/60',
          ringColor: 'ring-green-500/30',
          icon: <CheckCircle className="w-3 h-3" />,
          scale: intensity === 'high' ? [1, 1.2, 1] : [1, 1.1, 1],
          opacity: [0.8, 0.3, 0.8]
        }
      case 'reserved':
        return {
          color: 'from-blue-500/60 to-indigo-600/60',
          ringColor: 'ring-blue-500/30',
          icon: <Zap className="w-3 h-3" />,
          scale: [1, 1.15, 1],
          opacity: [0.7, 0.2, 0.7]
        }
      case 'viewing':
        return {
          color: 'from-purple-500/50 to-violet-600/50',
          ringColor: 'ring-purple-500/20',
          icon: <Eye className="w-3 h-3" />,
          scale: [1, 1.08, 1],
          opacity: [0.6, 0.1, 0.6]
        }
      case 'expiring':
        return {
          color: 'from-orange-500/70 to-red-500/70',
          ringColor: 'ring-orange-500/40',
          icon: <Clock className="w-3 h-3" />,
          scale: [1, 1.25, 1],
          opacity: [0.9, 0.4, 0.9]
        }
      default:
        return {
          color: 'from-gray-500/40 to-gray-600/40',
          ringColor: 'ring-gray-500/20',
          icon: <TrendingUp className="w-3 h-3" />,
          scale: [1, 1.1, 1],
          opacity: [0.5, 0.1, 0.5]
        }
    }
  }

  const getDuration = () => {
    switch (intensity) {
      case 'low': return 2
      case 'high': return 1.5
      default: return 1.8
    }
  }

  if (!isVisible) return null

  const config = getPulseConfig()

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className={`absolute inset-0 pointer-events-none ${className}`}
      >
        {/* Outer pulse ring */}
        <motion.div
          className={`absolute inset-0 rounded-xl bg-gradient-to-br ${config.color} ${config.ringColor} ring-2`}
          animate={{
            scale: config.scale,
            opacity: config.opacity
          }}
          transition={{
            duration: getDuration(),
            repeat: type === 'viewing' ? Infinity : 2,
            ease: "easeInOut"
          }}
        />

        {/* Inner glow */}
        <motion.div
          className={`absolute inset-2 rounded-lg bg-gradient-to-br ${config.color}`}
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.4, 0.1, 0.4]
          }}
          transition={{
            duration: getDuration() * 0.8,
            repeat: type === 'viewing' ? Infinity : 1,
            ease: "easeInOut",
            delay: 0.1
          }}
        />

        {/* Icon indicator */}
        {showIcon && (
          <motion.div
            className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 border border-gray-200"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 500, 
              damping: 30,
              delay: 0.2 
            }}
          >
            {config.icon}
          </motion.div>
        )}

        {/* Subtle shine effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-xl"
          animate={{
            x: ['-100%', '100%']
          }}
          transition={{
            duration: 1.5,
            ease: "easeInOut",
            delay: 0.3
          }}
        />
      </motion.div>
    </AnimatePresence>
  )
})

NumberPulse.displayName = 'NumberPulse'

// Component for managing multiple pulses on a number
interface NumberActivityOverlayProps {
  number: number
  activities: Array<{
    id: string
    type: NumberPulseProps['type']
    intensity?: NumberPulseProps['intensity']
    timestamp: number
  }>
  className?: string
}

export const NumberActivityOverlay = memo(({
  number,
  activities,
  className = ''
}: NumberActivityOverlayProps) => {
  const [activeActivities, setActiveActivities] = useState(activities)

  useEffect(() => {
    setActiveActivities(activities)
  }, [activities])

  const handlePulseComplete = (id: string) => {
    setActiveActivities(prev => prev.filter(activity => activity.id !== id))
  }

  if (activeActivities.length === 0) return null

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      <AnimatePresence>
        {activeActivities.map((activity, index) => (
          <NumberPulse
            key={activity.id}
            number={number}
            type={activity.type}
            intensity={activity.intensity}
            duration={3000 + (index * 500)} // Stagger multiple activities
            showIcon={index === 0} // Only show icon for the first activity
            className="absolute inset-0"
          />
        ))}
      </AnimatePresence>
    </div>
  )
})

NumberActivityOverlay.displayName = 'NumberActivityOverlay'

// Hook for managing number activities
export const useNumberActivity = () => {
  const [activities, setActivities] = useState<Map<number, Array<{
    id: string
    type: NumberPulseProps['type']
    intensity?: NumberPulseProps['intensity']
    timestamp: number
  }>>>(new Map())

  const addActivity = (
    number: number, 
    type: NumberPulseProps['type'], 
    intensity: NumberPulseProps['intensity'] = 'medium'
  ) => {
    const activityId = `${number}-${type}-${Date.now()}`
    const newActivity = {
      id: activityId,
      type,
      intensity,
      timestamp: Date.now()
    }

    setActivities(prev => {
      const updated = new Map(prev)
      const existing = updated.get(number) || []
      
      // Limit to 3 activities per number to avoid visual overload
      const filtered = existing.slice(-2)
      updated.set(number, [...filtered, newActivity])
      
      return updated
    })

    // Auto-cleanup after duration
    setTimeout(() => {
      setActivities(prev => {
        const updated = new Map(prev)
        const existing = updated.get(number) || []
        const filtered = existing.filter(a => a.id !== activityId)
        
        if (filtered.length === 0) {
          updated.delete(number)
        } else {
          updated.set(number, filtered)
        }
        
        return updated
      })
    }, 4000)
  }

  const clearActivities = (number?: number) => {
    if (number !== undefined) {
      setActivities(prev => {
        const updated = new Map(prev)
        updated.delete(number)
        return updated
      })
    } else {
      setActivities(new Map())
    }
  }

  return {
    activities,
    addActivity,
    clearActivities
  }
}

export default NumberPulse